import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Typography,
  Switch,
  CircularProgress,
  Tooltip,
} from "@material-ui/core";
import NumberFormat from "react-number-format";
import moment from "moment";
import { toast } from "react-toastify";
import { CalendarDays, Clock, FileText, AlertCircle, X as CloseIcon, Pencil, Plus, Trash2 } from "lucide-react";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(3),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  title: {
    fontSize: "1rem",
    fontWeight: 700,
    color: theme.palette.type === "dark" ? "#f8fafc" : "#1e293b",
  },
  subtitle: {
    fontSize: "0.8rem",
    color: theme.palette.type === "dark" ? "rgba(248,250,252,0.6)" : "#64748b",
    marginTop: 2,
  },
  addBtn: {
    textTransform: "none",
    borderRadius: 10,
    fontWeight: 500,
  },
  list: {
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.08)"
      : "1px solid #e2e8f0",
    borderRadius: 12,
    background: theme.palette.type === "dark" ? "#1e1e22" : "#ffffff",
    overflow: "hidden",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "130px 1fr 160px 100px",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    borderBottom: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.06)"
      : "1px solid #f1f5f9",
    "&:last-child": { borderBottom: "none" },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  rowHeader: {
    background: theme.palette.type === "dark"
      ? "rgba(255,255,255,0.04)"
      : "#f8fafc",
    fontSize: "0.75rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: theme.palette.type === "dark" ? "rgba(248,250,252,0.6)" : "#64748b",
  },
  dateCell: {
    fontWeight: 600,
    color: theme.palette.type === "dark" ? "#f8fafc" : "#1e293b",
    fontSize: "0.9rem",
  },
  statusBadge: {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: 999,
    fontSize: "0.72rem",
    fontWeight: 700,
  },
  statusClosed: {
    background: theme.palette.type === "dark" ? "rgba(239,68,68,0.15)" : "#fee2e2",
    color: theme.palette.type === "dark" ? "#fca5a5" : "#b91c1c",
  },
  statusOpen: {
    background: theme.palette.type === "dark" ? "rgba(34,197,94,0.15)" : "#dcfce7",
    color: theme.palette.type === "dark" ? "#86efac" : "#15803d",
  },
  note: {
    fontSize: "0.85rem",
    color: theme.palette.type === "dark" ? "rgba(248,250,252,0.7)" : "#64748b",
  },
  actions: {
    display: "flex",
    gap: theme.spacing(0.5),
    justifyContent: "flex-end",
  },
  emptyState: {
    padding: theme.spacing(3),
    textAlign: "center",
    color: theme.palette.type === "dark" ? "rgba(248,250,252,0.5)" : "#94a3b8",
    fontSize: "0.9rem",
  },
  loading: {
    padding: theme.spacing(4),
    textAlign: "center",
  },

  dialog: {
    "& .MuiDialog-paper": {
      borderRadius: 24,
      background: theme.palette.type === "dark"
        ? "linear-gradient(135deg, rgba(18,18,19,0.96) 0%, rgba(22,22,24,0.96) 100%)"
        : "#FFFFFF",
      border: theme.palette.type === "dark"
        ? "1px solid rgba(91,124,255,0.12)"
        : "none",
      boxShadow: theme.palette.type === "dark"
        ? "0 24px 48px rgba(0,0,0,0.6)"
        : "0 24px 48px rgba(0,0,0,0.12)",
      overflow: "hidden",
      position: "relative",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        background: theme.palette.type === "dark"
          ? "linear-gradient(90deg, #5B7CFF 0%, #9D7CFF 100%)"
          : "linear-gradient(90deg, #3D5CFD 0%, #7C3AED 100%)",
      },
    },
    "& .MuiBackdrop-root": {
      backgroundColor: theme.palette.type === "dark"
        ? "rgba(0,0,0,0.7)"
        : "rgba(0,0,0,0.4)",
    },
  },
  dialogTitle: {
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    borderBottom: theme.palette.type === "dark"
      ? "1px solid rgba(91,124,255,0.1)"
      : "1px solid rgba(0,0,0,0.06)",
  },
  titleIconBox: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, rgba(91,124,255,0.18) 0%, rgba(157,124,255,0.18) 100%)"
      : "linear-gradient(135deg, rgba(61,92,253,0.1) 0%, rgba(124,58,237,0.1) 100%)",
      color: theme.palette.type === "dark" ? "#93c5fd" : "#3D5CFD",
    flexShrink: 0,
  },
  titleTextBlock: {
    flex: 1,
    minWidth: 0,
  },
  titleHeading: {
    fontSize: "1.25rem",
    fontWeight: 700,
    color: theme.palette.type === "dark" ? "#f8fafc" : "#1e293b",
    lineHeight: 1.2,
  },
  titleSub: {
    fontSize: "0.8rem",
    color: theme.palette.type === "dark" ? "rgba(248,250,252,0.55)" : "#64748b",
    marginTop: 2,
  },
  closeBtn: {
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.5)" : "#94a3b8",
    "&:hover": {
      color: theme.palette.type === "dark" ? "#f8fafc" : "#1e293b",
      background: theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
    },
  },

  dialogContent: {
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    minWidth: 420,
    [theme.breakpoints.down("xs")]: { minWidth: "auto" },
  },
  fieldLabel: {
    fontSize: "0.72rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.45)" : "#94a3b8",
    marginBottom: theme.spacing(0.75),
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 14,
      background: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)",
      border: theme.palette.type === "dark"
        ? "1px solid rgba(91,124,255,0.15)"
        : "1px solid rgba(0,0,0,0.08)",
      transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
      "&:hover": {
        background: theme.palette.type === "dark" ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.04)",
        borderColor: theme.palette.type === "dark" ? "rgba(91,124,255,0.3)" : "rgba(0,0,0,0.12)",
      },
      "&.Mui-focused": {
        borderColor: theme.palette.type === "dark" ? "rgba(91,124,255,0.5)" : "#3D5CFD",
        boxShadow: theme.palette.type === "dark"
          ? "0 0 0 4px rgba(91,124,255,0.15)"
          : "0 0 0 4px rgba(61,92,253,0.1)",
      },
      "& fieldset": { border: "none" },
    },
  },
  switchRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.5, 2),
    borderRadius: 14,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(91,124,255,0.12)"
      : "1px solid rgba(0,0,0,0.06)",
  },
  switchLabelBlock: {
    display: "flex",
    flexDirection: "column",
  },
  switchLabelMain: {
    fontSize: "0.9rem",
    fontWeight: 600,
    color: theme.palette.type === "dark" ? "#f8fafc" : "#1e293b",
  },
  switchLabelHint: {
    fontSize: "0.75rem",
    color: theme.palette.type === "dark" ? "rgba(248,250,252,0.55)" : "#64748b",
  },
  timeRow: {
    display: "flex",
    gap: theme.spacing(1.5),
    alignItems: "center",
  },
  timeDash: {
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.3)" : "#94a3b8",
    fontWeight: 700,
  },
  errorBanner: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 12,
    background: theme.palette.type === "dark" ? "rgba(239,68,68,0.12)" : "rgba(239,68,68,0.08)",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(239,68,68,0.25)"
      : "1px solid rgba(239,68,68,0.2)",
    color: theme.palette.type === "dark" ? "#fca5a5" : "#b91c1c",
    fontSize: "0.85rem",
    fontWeight: 500,
  },

  dialogActions: {
    padding: theme.spacing(2, 3),
    borderTop: theme.palette.type === "dark"
      ? "1px solid rgba(91,124,255,0.1)"
      : "1px solid rgba(0,0,0,0.06)",
    gap: theme.spacing(1),
  },
  cancelBtn: {
    borderRadius: 12,
    textTransform: "none",
    fontWeight: 500,
    padding: "8px 24px",
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.7)" : "#64748b",
    borderColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.12)",
    "&:hover": {
      background: theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
      borderColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.2)",
    },
  },
  submitBtn: {
    borderRadius: 12,
    textTransform: "none",
    fontWeight: 500,
    padding: "8px 28px",
    color: "#ffffff",
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, #5B7CFF 0%, #9D7CFF 100%)"
      : "linear-gradient(135deg, #3D5CFD 0%, #7C3AED 100%)",
    boxShadow: theme.palette.type === "dark"
      ? "0 4px 16px rgba(91,124,255,0.3)"
      : "0 4px 16px rgba(61,92,253,0.25)",
    "&:hover": {
      background: theme.palette.type === "dark"
        ? "linear-gradient(135deg, #6B8CFF 0%, #AD8CFF 100%)"
        : "linear-gradient(135deg, #4D6CFD 0%, #8C4AED 100%)",
      boxShadow: theme.palette.type === "dark"
        ? "0 6px 24px rgba(91,124,255,0.4)"
        : "0 6px 24px rgba(61,92,253,0.35)",
    },
    "&.Mui-disabled": {
      background: theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)",
      color: theme.palette.type === "dark" ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.35)",
      boxShadow: "none",
    },
  },
}));

const INITIAL_FORM = {
  id: null,
  date: "",
  isClosed: true,
  startTime: "",
  endTime: "",
  note: "",
};

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

const ScheduleExceptionsManager = () => {
  const classes = useStyles();
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const mountedRef = useRef(true);

  useEffect(() => () => { mountedRef.current = false; }, []);

  const fetchExceptions = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/schedule-exceptions");
      if (mountedRef.current) setExceptions(data);
    } catch (err) {
      toastError(err);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchExceptions();
  }, [fetchExceptions]);

  const formError = useMemo(() => {
    if (!form.date) return "Informe a data";
    if (!form.isClosed) {
      if (!form.startTime || !form.endTime) return "Preencha hora inicial e final";
      if (!TIME_RE.test(form.startTime) || !TIME_RE.test(form.endTime)) {
        return "Formato HH:MM inválido";
      }
      if (form.startTime >= form.endTime) return "Hora final deve ser maior que a inicial";
    }
    return null;
  }, [form]);

  const handleOpenCreate = () => {
    setForm(INITIAL_FORM);
    setDialogOpen(true);
  };

  const handleOpenEdit = (ex) => {
    setForm({
      id: ex.id,
      date: ex.date,
      isClosed: ex.isClosed,
      startTime: ex.startTime || "",
      endTime: ex.endTime || "",
      note: ex.note || "",
    });
    setDialogOpen(true);
  };

  const handleClose = () => {
    if (saving) return;
    setDialogOpen(false);
    setForm(INITIAL_FORM);
  };

  const handleSave = async () => {
    if (formError) return;
    setSaving(true);
    try {
      const payload = {
        date: form.date,
        isClosed: form.isClosed,
        startTime: form.isClosed ? null : form.startTime,
        endTime: form.isClosed ? null : form.endTime,
        note: form.note || null,
      };
      if (form.id) {
        await api.put(`/schedule-exceptions/${form.id}`, payload);
        toast.success("Exceção atualizada");
      } else {
        await api.post("/schedule-exceptions", payload);
        toast.success("Exceção criada");
      }
      await fetchExceptions();
      setDialogOpen(false);
      setForm(INITIAL_FORM);
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remover esta exceção?")) return;
    try {
      await api.delete(`/schedule-exceptions/${id}`);
      toast.success("Exceção removida");
      await fetchExceptions();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div>
          <Typography className={classes.title}>
            Feriados e datas especiais
          </Typography>
          <Typography className={classes.subtitle}>
            Sobrepõem o horário semanal para a data escolhida.
          </Typography>
        </div>
        <Button
          variant="contained"
          color="primary"
          size="small"
          className={classes.addBtn}
          startIcon={<Plus />}
          onClick={handleOpenCreate}
        >
          Adicionar
        </Button>
      </div>

      {loading ? (
        <div className={classes.loading}>
          <CircularProgress size={24} />
        </div>
      ) : exceptions.length === 0 ? (
        <div className={classes.list}>
          <div className={classes.emptyState}>
            Nenhuma exceção cadastrada.
          </div>
        </div>
      ) : (
        <div className={classes.list}>
          <div className={`${classes.row} ${classes.rowHeader}`}>
            <div>Data</div>
            <div>Observação</div>
            <div>Status</div>
            <div style={{ textAlign: "right" }}>Ações</div>
          </div>
          {exceptions.map((ex) => (
            <div key={ex.id} className={classes.row}>
              <div className={classes.dateCell}>
                {moment(ex.date).format("DD/MM/YYYY")}
              </div>
              <div className={classes.note}>{ex.note || "—"}</div>
              <div>
                {ex.isClosed ? (
                  <span className={`${classes.statusBadge} ${classes.statusClosed}`}>
                    Fechado
                  </span>
                ) : (
                  <span className={`${classes.statusBadge} ${classes.statusOpen}`}>
                    {ex.startTime} – {ex.endTime}
                  </span>
                )}
              </div>
              <div className={classes.actions}>
                <Tooltip title="Editar">
                  <IconButton size="small" onClick={() => handleOpenEdit(ex)}>
                    <Pencil size={18} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remover">
                  <IconButton size="small" onClick={() => handleDelete(ex.id)}>
                    <Trash2 size={18} />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        className={classes.dialog}
      >
        <div className={classes.dialogTitle}>
          <div className={classes.titleIconBox}>
            <CalendarDays size={22} strokeWidth={1.8} />
          </div>
          <div className={classes.titleTextBlock}>
            <Typography className={classes.titleHeading}>
              {form.id ? "Editar exceção" : "Nova exceção"}
            </Typography>
            <Typography className={classes.titleSub}>
              Sobrepõe o horário semanal para uma data específica.
            </Typography>
          </div>
          <IconButton
            size="small"
            onClick={handleClose}
            disabled={saving}
            className={classes.closeBtn}
          >
            <CloseIcon size={18} strokeWidth={1.8} />
          </IconButton>
        </div>

        <DialogContent className={classes.dialogContent}>
          <div>
            <div className={classes.fieldLabel}>
              <CalendarDays size={12} strokeWidth={2} />
              Data
            </div>
            <TextField
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              variant="outlined"
              className={classes.textField}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </div>

          <div className={classes.switchRow}>
            <div className={classes.switchLabelBlock}>
              <span className={classes.switchLabelMain}>Fechado o dia todo</span>
              <span className={classes.switchLabelHint}>
                {form.isClosed
                  ? "Nenhum atendimento nesta data."
                  : "Usa horário reduzido abaixo."}
              </span>
            </div>
            <Switch
              checked={form.isClosed}
              onChange={(e) =>
                setForm({
                  ...form,
                  isClosed: e.target.checked,
                  startTime: "",
                  endTime: "",
                })
              }
              color="primary"
            />
          </div>

          {!form.isClosed && (
            <div>
              <div className={classes.fieldLabel}>
                <Clock size={12} strokeWidth={2} />
                Horário de atendimento
              </div>
              <div className={classes.timeRow}>
                <NumberFormat
                  value={form.startTime}
                  onValueChange={({ formattedValue }) =>
                    setForm({ ...form, startTime: formattedValue })
                  }
                  variant="outlined"
                  customInput={TextField}
                  className={classes.textField}
                  format="##:##"
                  placeholder="09:00"
                  style={{ flex: 1 }}
                />
                <span className={classes.timeDash}>–</span>
                <NumberFormat
                  value={form.endTime}
                  onValueChange={({ formattedValue }) =>
                    setForm({ ...form, endTime: formattedValue })
                  }
                  variant="outlined"
                  customInput={TextField}
                  className={classes.textField}
                  format="##:##"
                  placeholder="14:00"
                  style={{ flex: 1 }}
                />
              </div>
            </div>
          )}

          <div>
            <div className={classes.fieldLabel}>
              <FileText size={12} strokeWidth={2} />
              Observação (opcional)
            </div>
            <TextField
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              variant="outlined"
              className={classes.textField}
              fullWidth
              placeholder="Ex: Natal, Ano Novo, meio expediente"
            />
          </div>

          {formError && (
            <div className={classes.errorBanner}>
              <AlertCircle size={16} strokeWidth={2} />
              {formError}
            </div>
          )}
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <Button
            onClick={handleClose}
            disabled={saving}
            variant="outlined"
            className={classes.cancelBtn}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!!formError || saving}
            className={classes.submitBtn}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {saving ? "Salvando..." : form.id ? "Salvar alterações" : "Criar exceção"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ScheduleExceptionsManager;
